import times from 'lodash/times';

import dayjs from 'utils/dayjs';

export const AVAILABLE_TIMEZONES = {
  'Pacific/Honolulu': `(GMT${dayjs().tz('Pacific/Honolulu').format('Z')}) Hawaii`,
  'America/Juneau': `(GMT${dayjs().tz('America/Juneau').format('Z')}) Alaska`,
  'America/Los_Angeles': `(GMT${dayjs().tz('America/Los_Angeles').format('Z')}) Pacific Time (US & Canada)`,
  'America/Denver': `(GMT${dayjs().tz('America/Denver').format('Z')}) Mountain Time (US & Canada)`,
  'America/Phoenix': `(GMT${dayjs().tz('America/Phoenix').format('Z')}) Arizona`,
  'America/Regina': `(GMT${dayjs().tz('America/Regina').format('Z')}) Saskatchewan`,
  'America/Chicago': `(GMT${dayjs().tz('America/Chicago').format('Z')}) Central Time (US & Canada)`,
  'America/Indiana/Indianapolis': `(GMT${dayjs().tz('America/Indiana/Indianapolis').format('Z')}) Indiana (East)`,
  'America/New_York': `(GMT${dayjs().tz('America/New_York').format('Z')}) Eastern Time (US & Canada)`,
  'America/Halifax': `(GMT${dayjs().tz('America/Halifax').format('Z')}) Atlantic Time (Canada)`,
  'America/St_Johns': `(GMT${dayjs().tz('America/St_Johns').format('Z')}) Newfoundland`,
};

export const AVAILABLE_MINIMUM_TIME_IN_MINUTES = times(12, (i) => `${(i + 1) * 5} min`);

const EXPERT_AVAILABILITY_SECTION_HREF = '#availability';

export const expertAvailability = {
  FLEXIBLE: 'flexible',
  SPECIFIC_DATES: 'specific_dates',
  SCHEDULED: 'scheduled',
};

export const tooltipConfig = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 4],
      },
    },
  ],
};

export const CALL_REQUEST_TAGS = {
  waitlisted: 'waitlisted',
  original: 'original',
  waitlistPromoted: 'waitlist_promoted',
  waitlistDeclined: 'waitlist_declined',
};

// Mirrors backend URI::MailTo::EMAIL_REGEXP semantics with one stricter UX
// rule: requires at least one TLD label.
export const EMAIL_PATTERN = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;

export const EMAIL_MAX_LENGTH = 254; // RFC 5321

export const UNPROCESSABLE_ENTITY_STATUS = 422;
export const LOCKED_STATUS = 423;

export const REQUIRED_EMAIL_RULES = {
  required: 'Your email is required',
  maxLength: { value: EMAIL_MAX_LENGTH, message: 'Email is too long' },
  pattern: { value: EMAIL_PATTERN, message: 'Please enter a valid email address' },
};

export const OPTIONAL_EMAIL_RULES = {
  maxLength: { value: EMAIL_MAX_LENGTH, message: 'Email is too long' },
  pattern: { value: EMAIL_PATTERN, message: 'Please enter a valid email address' },
};

export const urlConfig = {
  pattern: {
    value: RegExp(String.raw`^((https?|ftp)://|[^\-][^\s/?\.#-,@]*\.)(-\.)?([^\s/?\.#-,@]+\.?)+(/[^\s]*)?$`),
    message: 'Invalid URL',
  },
};

export const passwordConfig = {
  minLength: {
    value: 8,
    message: 'Password must be at least 8 characters',
  },
  pattern: {
    value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/,
    message: 'Password must contain at least 1 lower case, 1 upper case, and 1 numeric character',
  },
};

export const accountGeneralSections = {
  USER_PROFILE: {
    title: 'User Profile',
    href: '#profile',
  },
  PROFILE_PICTURES: {
    title: 'Profile Pictures',
    href: '#photo',
  },
  PASSWORD: {
    title: 'Password',
    href: '#security',
  },
  NOTIFICATIONS: {
    title: 'Notifications',
    href: '#preferences',
  },
  PAYMENT_METHODS: {
    title: 'Payment Methods',
    href: '#payment-methods',
  },
  TERMS_OR_SERVICES: {
    title: 'Terms of Service',
    href: '#accept-terms',
  },
  SIGN_IN_SECURITY: {
    title: 'Sign-In & Security',
    href: '#sign-in-security',
  },
  TROUBLESHOOTING: {
    title: 'Troubleshooting',
    href: '#user-troubleshooting',
  },
  DEVICES_PREVIEW: {
    title: 'Media Devices Preview',
    href: '#devices-preview',
  },
};

export const accountExpertSections = {
  EXPERT_DETAILS: {
    title: 'Expert Details',
    href: '#details',
  },
  AVAILABILITY: {
    title: 'Availability',
    href: EXPERT_AVAILABILITY_SECTION_HREF,
  },
  SESSIONS: {
    title: 'Sessions',
    href: '#sessions',
  },
  SESSION_PACKS: {
    title: 'Session Packs',
    href: '#packs',
  },
  GIFT_CERTIFICATES: {
    title: 'Gift Certificates',
    href: '#gift-certificates',
  },
  CALENDAR_CONNECTIONS: {
    title: 'Calendar Connections',
    href: '#calendar-connections',
  },
  CURRENCY_LIMITATIONS: {
    title: 'Currency Limitations',
    href: '#currency-limitations',
  },
  PAYOUT_METHODS: {
    title: 'Payout and Charities',
    href: '#payout-methods',
  },
  ZOOM_INTEGRATION: {
    title: 'Zoom Integration',
    href: '#zoom-account',
  },
  IN_PERSON_MEETING_LOCATIONS: {
    title: 'In-Person Meeting Locations',
    href: '#in-person-meeting-locations',
  },
  AFFILIATE_PROFILE: {
    title: 'Affiliate Profile',
    href: '#affiliate-profile',
  },
  CUSTOM_COLORS: {
    title: 'Custom Colors',
    href: '#custom-colors',
  },
};

export const ACCOUNT_EXPERT_GLOBAL_SECTIONS = {
  vacationMode: {
    title: 'Vacation Mode',
    href: '#vacation-mode',
  },
  sites: {
    title: 'Sites',
    href: '#expert-sites',
  },
  volunteerOpportunities: {
    title: 'Volunteer Opportunities',
    href: '#volunteer-opportunities',
  },
  fundraisers: {
    title: 'Fundraisers',
    href: '#fundraisers',
  },
  troubleshooting: {
    title: 'Troubleshooting',
    href: '#expert-troubleshooting',
  },
  accessManagement: {
    title: 'Access Management',
    href: '#access-management',
  },
};

export const SERVER = 'server';

export const generalColors = {
  PRIMARY: 'var(--primary-color)',
  LIGHT_GRAY: 'var(--light-gray)',
  GRAY: 'var(--gray-color)',
  ACCENT: 'var(--accent-color)',
};

export const generalFonts = {
  NUMBER: 'Arial',
};

export const TIME_SLOTS_LIST = [
  '12:00 AM',
  '12:30 AM',
  '01:00 AM',
  '01:30 AM',
  '02:00 AM',
  '02:30 AM',
  '03:00 AM',
  '03:30 AM',
  '04:00 AM',
  '04:30 AM',
  '05:00 AM',
  '05:30 AM',
  '06:00 AM',
  '06:30 AM',
  '07:00 AM',
  '07:30 AM',
  '08:00 AM',
  '08:30 AM',
  '09:00 AM',
  '09:30 AM',
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '11:30 AM',
  '12:00 PM',
  '12:30 PM',
  '01:00 PM',
  '01:30 PM',
  '02:00 PM',
  '02:30 PM',
  '03:00 PM',
  '03:30 PM',
  '04:00 PM',
  '04:30 PM',
  '05:00 PM',
  '05:30 PM',
  '06:00 PM',
  '06:30 PM',
  '07:00 PM',
  '07:30 PM',
  '08:00 PM',
  '08:30 PM',
  '09:00 PM',
  '09:30 PM',
  '10:00 PM',
  '10:30 PM',
  '11:00 PM',
  '11:30 PM',
];

export const TIME_MENU = TIME_SLOTS_LIST.map((time) => time.replace(' ', '').toLowerCase());

export const CALLS_DASHBOARD_SECTIONS = {
  current: {
    title: 'Current',
    href: '#current',
  },
  completed: {
    title: 'Completed',
    href: '#completed',
  },
  history: {
    title: 'History',
    href: '#past',
  },
  waitlist: {
    title: 'Waitlist',
    href: '#waitlist',
    requiredExpert: true,
  },
  testing: {
    title: 'Testing',
    href: '#testing',
  },
};

export const CALL_REQUEST_STATUSES = {
  aborted: 'aborted',
  pendingExpertAcceptance: 'pending_expert_acceptance',
  pendingRequester: 'pending_requester',
  scheduled: 'scheduled',
  rescheduled: 'rescheduled',
  live: 'live',
  extended: 'extended',
  paused: 'paused',
  completed: 'completed',
  declined: 'declined',
  canceled: 'canceled',
  missed: 'missed',
  incomplete: 'incomplete',
  completedPendingPayment: 'completed_pending_payment',
  pendingConnection: 'pending_connection',
  markedComplete: 'marked_complete',
  refunded: 'refunded',
  expertNoResponse: 'expert_no_response',
  userNoResponse: 'user_no_response',
  pendingConfirmation: 'pending_confirmation',
  pendingUserConfirmation: 'pending_user_confirmation',
  pendingExpertConfirmation: 'pending_expert_confirmation',
};

export const EXPERT_CALL_STATUSES = {
  expertCalled: 'expert_called',
  expertUncalled: 'expert_uncalled',
  expertBusy: 'expert_busy',
  expertFailed: 'expert_failed',
  expertNoAnswer: 'expert_no_answer',
  expertCanceled: 'expert_canceled',
  expertConnected: 'expert_connected',
  expertCompleted: 'expert_completed',
};

export const REQUESTER_CALL_STATUSES = {
  requesterCalled: 'requester_called',
  requesterUncalled: 'requester_uncalled',
  requesterBusy: 'requester_busy',
  requesterFailed: 'requester_failed',
  requesterNoAnswer: 'requester_no_answer',
  requesterCanceled: 'requester_canceled',
  requesterConnected: 'requester_connected',
  requesterCompleted: 'requester_completed',
};

export const MISMATCHED_PASSWORDS_ERROR = 'The password confirmation does not match.';

export const CHARGE_STATUSES = {
  authorized: 'authorized',
  finalized: 'finalized',
  initialized: 'initialized',
};

export const CALL_REQUEST_ACTION_SLUGS = {
  edit: 'edit',
  reschedule: 'reschedule',
};

export const OPEN_ENDED_SESSION = {
  audio: true,
  name: 'Open-Ended Conversation',
  description:
    'Don’t know where to start or have multiple questions ' +
    'or topics you’ll like to discuss? ' +
    'Don’t worry about it, our expert has Open Ended Conversations so you can take as much time ' +
    'and go as in depth as you need so you can become your very best.',
};

export const ACCOUNT_TYPES = {
  visitor: 'Visitor',
  learner: 'Learner',
  expert: 'Expert',
};

export const ONBOARDING_SECTIONS = {
  welcome: {
    title: 'Welcome',
    href: '#welcome',
  },
  acceptTerms: {
    title: 'Terms of Service',
    href: '#accept-terms',
    step: 'onboard_accept_terms_visited',
    completeCheckField: 'acceptedTerms',
    visibilityCheckField: 'acceptTerms',
  },
  aboutMe: {
    title: 'About me',
    href: '#about-me',
    step: 'onboard_about_me_visited',
    completeCheckField: 'userProfile',
    visibilityCheckField: 'aboutMe',
  },
  profilePhotos: {
    title: 'Profile photos',
    href: '#profile-photos',
    step: 'onboard_profile_photos_visited',
    completeCheckField: 'avatar',
    visibilityCheckField: 'profilePhotos',
  },
  paymentMethod: {
    title: 'Payment method',
    href: '#payment-method',
    step: 'onboard_payment_method_visited',
    completeCheckField: 'paymentDetails',
    visibilityCheckField: 'paymentMethod',
  },
  payout: {
    title: 'Payout and Charities',
    href: '#payout-method',
    step: 'account_receive_payments_visited',
    requiredExpert: true,
    completeCheckField: 'payable',
    visibilityCheckField: 'payout',
  },
  expertise: {
    title: 'Expertise',
    href: '#expertise',
    step: 'onboard_expertise_visited',
    requiredExpert: true,
    completeCheckField: 'expertDetails',
    visibilityCheckField: 'expertise',
  },
  availability: {
    title: 'Availability',
    href: EXPERT_AVAILABILITY_SECTION_HREF,
    step: 'profiles_schedule_visited',
    requiredExpert: true,
    completeCheckField: 'availability',
    visibilityCheckField: 'availability',
  },
  sessions: {
    title: 'Sessions',
    href: '#sessions',
    step: 'call_types_index_visited',
    completeCheckField: 'sessions',
    visibilityCheckField: 'sessions',
    requiredExpert: true,
  },
  phoneVerification: {
    title: 'Phone Number',
    href: '#phone-verification',
    step: 'phone_verification_visited',
    completeCheckField: 'phoneVerified',
    visibilityCheckField: 'phoneNumber',
  },
  volunteerOpportunities: {
    title: 'Volunteer Opportunities',
    href: '#volunteer-opportunities',
    step: 'volunteer_opportunities_visited',
    requiredExpert: true,
    completeCheckField: 'volunteerOpportunities',
    visibilityCheckField: 'volunteerOpportunities',
  },
  charityContribution: {
    title: 'Charity Contribution',
    href: '#charity-contribution',
    step: 'charity_contribution_visited',
    requiredExpert: true,
    completeCheckField: 'charityContribution',
    visibilityCheckField: 'charityContribution',
  },
};

export const DEFAULT_USER_SIGN_UP = {
  firstName: '',
  lastName: '',
  middleName: '',
  pronoun: null,
  email: '',
  password: '',
  passwordConfirmation: '',
  timezoneIdx: 0,
  expertInvitationCode: '',
  timezone: 'Pacific/Honolulu',
  userInvitationCode: '',
};

export const ONBOARDING_STEP_VISBILITIES = {
  visible: 0,
  hidden: 1,
  locked: 2,
};

export const EXPERT_STATUS = {
  approved: 'approved',
  pendingExpertApproval: 'pending_expert_approval',
};

export const CALL_REQUEST_LOG_TYPES = {
  unknown: 'unknown',
  requestedAChange: 'requested_a_change',
  updatedStatus: 'updated_status',
  noResponse: 'no_response',
};

export const DEFAULT_LANGUAGE = {
  id: 0,
  name: 'English',
  symbol: 'EN',
  default: true,
};

export const MAX_PHOTO_SIZE = 2097152;

export const EXCEED_MAX_PHOTO_SIZE_ERROR = 'Please upload an image smaller than 2MB';

export const BUBBLE_ICONS_TYPES = {
  invisible: 'invisible',
  audio: 'audio',
  video: 'video',
  inPersonMeeting: 'inPersonMeeting',
  groupVideoconference: 'groupVideoconference',
};

export const SAMPLE_TEXT = 'Lorem ipsum';

export const SUPPORTING_VIDEO_SOURCES = {
  youtube: ['youtube.com', 'youtu.be'],
  vimeo: ['vimeo.com'],
};

export const INVALID_TOKEN_ERROR_MESSAGE = 'Unable to verify your identity. Please authenticate and try again';
// Shared by the code step and the toast reducer: after a code the login link is replayed and
// re-mints the session, so both would toast — one id, one toast
export const SIGN_IN_TOAST_ID = 'signInSuccess';

export const INVALID_REQUEST_MESSAGE =
  "Oops! We couldn't process your request. Please make sure your input follows the correct format.";
